// Iter BA — in-app Square card entry for subscriptions (Web Payments SDK).
import { useEffect, useRef, useState } from "react";
import { X, Lock } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";

const SDK_URLS = {
  production: "https://web.squarecdn.com/v1/square.js",
  sandbox: "https://sandbox.web.squarecdn.com/v1/square.js",
};

const loadSquareSdk = (env) =>
  new Promise((resolve, reject) => {
    if (window.Square) return resolve(window.Square);
    const s = document.createElement("script");
    s.src = SDK_URLS[env] || SDK_URLS.production;
    s.onload = () => resolve(window.Square);
    s.onerror = () => reject(new Error("Could not load the Square payment form"));
    document.head.appendChild(s);
  });

export const SquareSubscribeModal = ({ open, onClose, tier, squareConfig, onSuccess }) => {
  const { user } = useAuth();
  const containerRef = useRef(null);
  const cardRef = useRef(null);
  const [ready, setReady] = useState(false);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open || !squareConfig?.available) return undefined;
    let cancelled = false;
    setReady(false);
    setError("");
    (async () => {
      try {
        const Square = await loadSquareSdk(squareConfig.environment);
        const payments = Square.payments(squareConfig.application_id, squareConfig.location_id);
        const card = await payments.card();
        if (cancelled) { card.destroy(); return; }
        await card.attach(containerRef.current);
        cardRef.current = card;
        setReady(true);
      } catch (e) {
        if (!cancelled) setError(e.message || "Payment form failed to load");
      }
    })();
    return () => {
      cancelled = true;
      if (cardRef.current) { cardRef.current.destroy(); cardRef.current = null; }
    };
  }, [open, squareConfig]);

  if (!open || !tier) return null;

  const price = (tier.priceCents / 100).toFixed(2);

  const pay = async () => {
    if (!cardRef.current) return;
    setBusy(true);
    try {
      const nameParts = (user?.name || "").split(" ");
      const result = await cardRef.current.tokenize({
        amount: price,
        currencyCode: "USD",
        intent: "STORE",
        customerInitiated: true,
        sellerKeyedIn: false,
        billingContact: {
          givenName: nameParts[0] || "Posturra",
          familyName: nameParts.slice(1).join(" ") || "Member",
          email: user?.email,
        },
      });
      if (result.status !== "OK") {
        const msg = (result.errors || []).map((e) => e.message).join(", ") || "Card was declined";
        throw new Error(msg);
      }
      await api.post("/billing/subscribe", { tier: tier.code, source_id: result.token });
      toast.success(`Welcome to ${tier.label}! Your subscription is active.`);
      onSuccess();
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message || "Subscription failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[70] bg-ink-900/50 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4" data-testid="square-subscribe-modal">
      <div className="bg-white w-full sm:max-w-md rounded-t-3xl sm:rounded-3xl border border-paper-200 p-6 sm:p-7">
        <div className="flex items-start justify-between mb-1">
          <div className="font-heading text-xl text-ink-900">Subscribe to {tier.label}</div>
          <button data-testid="square-modal-close" onClick={onClose} className="w-9 h-9 rounded-full hover:bg-paper-100 flex items-center justify-center text-ink-500 shrink-0">
            <X className="w-4 h-4" strokeWidth={1.5} />
          </button>
        </div>
        <p className="text-sm text-ink-500 mb-5">
          ${price}/month, billed monthly. Cancel anytime — you keep access until the end of your billing period.
          {tier.code === "founding" && " As a Founding Member you keep this rate for as long as your subscription stays active."}
        </p>

        {error ? (
          <div className="text-sm text-terracotta-600 bg-terracotta-500/5 border border-terracotta-500/30 rounded-2xl p-4 mb-4" data-testid="square-form-error">{error}</div>
        ) : (
          <>
            <div ref={containerRef} data-testid="square-card-container" className="mb-4 min-h-[92px]">
              {!ready && <div className="text-xs text-ink-500 animate-pulse py-8 text-center">Loading secure payment form…</div>}
            </div>
            <button
              data-testid="square-pay-btn"
              onClick={pay}
              disabled={!ready || busy}
              className="w-full min-h-[50px] rounded-full bg-ink-900 hover:bg-ink-800 disabled:opacity-50 text-white font-medium text-sm transition-all"
            >
              {busy ? "Processing…" : `Subscribe — $${price}/mo`}
            </button>
          </>
        )}
        <p className="text-[10px] text-ink-500 mt-3 text-center inline-flex items-center justify-center gap-1 w-full">
          <Lock className="w-3 h-3" strokeWidth={1.5} /> Payments processed securely by Square. Card details never touch Posturra servers.
        </p>
      </div>
    </div>
  );
};
